import { css } from '@emotion/react';
import { useIntakeFormShallowStore } from '@frontend/api-intake-form';
import { useAccessibilityModeEnabled } from '@frontend/accessibility';
import { getUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { HOTSPOT_TARGET, OnboardingHotspot } from '@frontend/product-onboarding';
import { useNavSize } from '@frontend/responsiveness';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import {
  Avatar,
  NakedButton,
  PopoverMenu,
  PopoverMenuItem,
  styles,
  Text,
  usePopoverMenu,
  useTooltip,
} from '@frontend/design-system';
import { useApp } from '../../../providers/app.provider';
import { dispatchTopBarModalClose } from '../events/modal-events';
import { useWorkspaceInfo } from '../hooks/use-workspace-info';
import { useTopBarShallowStore } from '../store/use-top-bar-store';
import { iconButtonStyles, navItemStyles, popoverItemStyles } from '../styles/styles';
import { NotificationButton } from './notifications/notification-button';

export const TopBarMobileControls = () => {
  const workspaceInfo = useWorkspaceInfo();
  const { navigate: settingsNavigate } = useSettingsNavigate();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const { currentChannel } = useApp();
  const hideNotificationBell = getFeatureFlagValue('hideNotificationBell');
  const user = getUser();
  const { isShowIntakeForm } = useIntakeFormShallowStore('isShowIntakeForm');
  const {
    selectedIcon,
    handleSelectableIconClick: handleIconClick,
    nextVersion,
  } = useTopBarShallowStore('selectedIcon', 'handleSelectableIconClick', 'nextVersion');
  const { selectedLocationIds, getLocationName } = useAppScopeStore();
  const { t } = useTranslation();
  const navSize = useNavSize();
  const isXsmall = navSize.isLte('xsmall');
  const isMobile = navSize.isLte('mobile');
  const singleLocationReferenceName = getLocationName(selectedLocationIds[0]);

  const { Tooltip, tooltipProps, triggerProps } = useTooltip({ placement: 'bottom' });

  const isInWorkspaceWithName = workspaceInfo?.isWorkspaceMode && !!workspaceInfo.workspaceName;

  const locationText = isInWorkspaceWithName ? (
    <span {...triggerProps}>
      <Text
        css={navItemStyles({ isXsmall, isMobile, isSelected: 'location' === selectedIcon, truncate: styles.truncate })}
      >
        {workspaceInfo.workspaceName}
      </Text>
      <Text
        css={css`
          font-size: ${theme.fontSize(11)};
        `}
        size='small'
        color='subdued'
      >
        {selectedLocationIds.length} {selectedLocationIds.length === 1 ? t('Location') : t('Locations')}
      </Text>
      <Tooltip
        {...tooltipProps}
        css={css`
          text-align: center;
          display: flex;
          flex-direction: column;
          gap: ${theme.spacing(0.25)};
        `}
      >
        <Text size='small' color='white' weight='bold'>
          {t('Workspace mode is active')}
        </Text>
        <Text
          size='small'
          color='white'
          css={css`
            opacity: 0.85;
          `}
        >
          {t('To exit, manually choose and apply a location from the location picker.')}
        </Text>
      </Tooltip>
    </span>
  ) : (
    <Text
      css={navItemStyles({ isXsmall, isMobile, isSelected: 'location' === selectedIcon, truncate: styles.truncate })}
    >
      {selectedLocationIds.length > 1
        ? `${selectedLocationIds.length} Locations`
        : singleLocationReferenceName || t('No location selected')}
    </Text>
  );
  const {
    getTriggerProps: getMiniPopoverTriggerProps,
    getMenuProps: getMiniPopverMenuProps,
    getItemProps: getMinPopverItemProps,
    close: closeMiniPopover,
    isOpen: isMiniPopoverOpen,
  } = usePopoverMenu({ placement: 'bottom-end' });
  const { isEnabled: accessibilityModeEnabled } = useAccessibilityModeEnabled('isEnabled');

  return (
    <>
      {!hideNotificationBell && (
        <div
          css={css`
            position: relative;
          `}
        >
          <NotificationButton />
        </div>
      )}
      <div>
        <NakedButton
          {...getMiniPopoverTriggerProps({
            onClick: dispatchTopBarModalClose,
          })}
          css={[
            iconButtonStyles({ isSelected: selectedIcon === 'profile' || isMiniPopoverOpen, isMobile }),
            css`
              position: relative;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: ${theme.spacing(0.5)};
            `,
          ]}
          trackingId='top-bar-profile-dropdown'
        >
          <Avatar
            isUser
            name={user?.firstName + ' ' + user?.lastName}
            size='xs'
            disableClick
            css={css`
              > div {
                border: ${currentChannel?.slug === 'edge' ? '1px solid black' : 'none'};
              }
            `}
          >
            {(nextVersion || accessibilityModeEnabled) && (
              <div
                css={css`
                  position: absolute;
                  top: 0;
                  right: 0;
                  width: ${theme.spacing(1.5)};
                  height: ${theme.spacing(1.5)};
                  background: ${theme.colors.status.critical};
                  border-radius: 50%;
                  border: 1.5px solid ${theme.colors.white};
                `}
              />
            )}
          </Avatar>
          <div>
            <Icon
              className='mini-display__icon'
              css={css`
                transition: transform 0.3s ease-out;
                transform: rotate(${isMiniPopoverOpen ? '180deg' : '0deg'});
              `}
              size={8}
              name='alt-caret-down-tiny'
            />
          </div>
        </NakedButton>
        <PopoverMenu
          {...getMiniPopverMenuProps()}
          css={css`
            width: 212px;
          `}
        >
          <PopoverMenuItem
            className='mini-popover__item'
            css={popoverItemStyles()}
            {...getMinPopverItemProps({ index: 0 })}
            Icon={() => <Icon size={16} name='user' />}
            onClick={(e: any) => {
              handleIconClick('profile', e);
              closeMiniPopover();
            }}
            trackingId='top-bar-profile-dropdown--profile'
          >
            <span>{t('My profile')}</span>
          </PopoverMenuItem>
          <PopoverMenuItem
            className='mini-popover__item'
            css={popoverItemStyles()}
            {...getMinPopverItemProps({ index: 1 })}
            Icon={() => <Icon size={16} name={isInWorkspaceWithName ? 'bookmark-small' : 'location'} />}
            onClick={(e: any) => {
              handleIconClick('location', e);
              closeMiniPopover();
            }}
            trackingId='top-bar-profile-dropdown--location'
          >
            {locationText}
          </PopoverMenuItem>
          {!isShowIntakeForm && (
            <OnboardingHotspot target={HOTSPOT_TARGET.TOP_BAR_SETTINGS_ICON} offset={{ top: 12, right: 90 }}>
              <PopoverMenuItem
                className='mini-popover__item'
                css={popoverItemStyles()}
                {...getMinPopverItemProps({ index: 2 })}
                Icon={() => <Icon size={16} name='settings' />}
                onClick={() => {
                  settingsNavigate({ to: '/organization' });
                  closeMiniPopover();
                }}
                trackingId='top-bar-profile-dropdown--settings'
              >
                <span>{t('Settings')}</span>
              </PopoverMenuItem>
            </OnboardingHotspot>
          )}
        </PopoverMenu>
      </div>
    </>
  );
};

export const TopBarMobileControlsWrapper = () => {
  const navSize = useNavSize();
  const isXsmall = navSize.isLte('xsmall');

  if (!isXsmall) return null;

  return <TopBarMobileControls />;
};
